import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { toast } from 'react-toastify';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import * as adminActions from '../../actions/admin';
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import ProcessPicker, { ProcessPickerOption } from '../../components/ProcessPicker/ProcessPicker';
import ProcessOrderStrip, { ProcessOrderStripItem } from '../../components/ProcessOrderStrip/ProcessOrderStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import { axiosCaller } from '../../utils/apiCaller';
import { t } from '../../i18n';
import './materials.scss';

interface MaterialDetailed {
    id: number;
    // Column is named 'material' (not 'name') — mirrors the existing
    // MaterialResource / MaterialWithResponsibilitiesResource shape so that
    // Articles dropdowns reading state.admin.materials don't break.
    material: string;
    articles_count: number;
    responsibilities_count: number;
    can_delete: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface ResponsibilityOption {
    value?: number;
    id?: number;
    label?: string;
}

interface SelectedMaterial {
    value?: number;
    label?: string;
    responsibilities?: ResponsibilityOption[];
}

interface Props {
    history: any;
    actions: any;
    productionActions: any;
    adminActions: any;
    materialsDetailed: MaterialDetailed[];
    responsibilities: ResponsibilityOption[];
    selectedMaterial: SelectedMaterial | null;
    kubitEnabled?: boolean;
    loading: boolean;
    buttonLoader: boolean;
}

interface KubitRawItem {
    value: number;
    label: string;
    code?: string;
}

type ModalMode = 'closed' | 'create' | 'edit';

const Materials = (props: Props) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [search, setSearch] = useState('');
    // Pagination: default 20. Convention from the shared PaginationComponent
    // — `pageSize === 0` means "All" (the component's All option uses value 0).
    // Reset to page 1 whenever the underlying filter or page size changes.
    const [pageSize, setPageSize] = useState<number>(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [modalMode, setModalMode] = useState<ModalMode>('closed');
    const [editingMaterial, setEditingMaterial] = useState<MaterialDetailed | null>(null);
    const [modalName, setModalName] = useState('');
    const [modalResponsibilityIds, setModalResponsibilityIds] = useState<number[]>([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState<MaterialDetailed | null>(null);

    // Track which material's detail we already populated so we don't re-apply
    // the same payload on every selectedMaterial change. Keyed by material id.
    const [populatedFromId, setPopulatedFromId] = useState<number | null>(null);

    // KuBIT raw-material link state (only used for KuBIT-enabled companies):
    // the type-3 items to pick from, and a materialId→kubitItemId link map.
    const [kubitItems, setKubitItems] = useState<KubitRawItem[]>([]);
    const [kubitLinks, setKubitLinks] = useState<Record<number, number>>({});

    useEffect(() => {
        props.adminActions.getAllMaterialsDetailed();
        // Process picker source: production.responsibilities. Loaded once on mount.
        props.productionActions.fetchResponsibilities();
    }, []);

    useEffect(() => {
        if (!props.kubitEnabled) return;
        axiosCaller('GET', 'admin/integrations/kubit/raw-materials')
            .then((res: any) => {
                setKubitItems(res?.data?.items || []);
                const links = res?.data?.links || {};
                const norm: Record<number, number> = {};
                Object.keys(links).forEach((k) => { norm[Number(k)] = Number(links[k]); });
                setKubitLinks(norm);
            })
            .catch(() => undefined);
    }, [props.kubitEnabled]);

    // Optimistic link/unlink; reverts + toasts on failure.
    const handleKubitLink = (materialId: number, kubitItemId: number | null) => {
        const prev = kubitLinks;
        setKubitLinks((s) => {
            const next = { ...s };
            if (kubitItemId === null) delete next[materialId];
            else next[materialId] = kubitItemId;
            return next;
        });
        axiosCaller('POST', 'admin/integrations/kubit/material-link', { materialId, kubitItemId })
            .catch(() => {
                setKubitLinks(prev);
                toast.error(fm('admin.materials.kubit.linkFailed', 'Lidhja me KuBIT dështoi'));
            });
    };

    const refresh = () => props.adminActions.getAllMaterialsDetailed();

    const list = props.materialsDetailed || [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(m => (m.material || '').toLowerCase().includes(q));
    }, [list, search]);

    const isInitialLoad = props.loading && list.length === 0;

    // ── Pagination math ──
    // safePage clamps currentPage against the current page count so a stale
    // currentPage (e.g. after a delete that shortens the list, or a search
    // that returns fewer rows) doesn't render an empty page. The
    // PaginationComponent expects a `lastPage` value and prev/next callbacks
    // that take the target page directly, so we just pass `safePage` and the
    // page count and let it handle the rest.
    const total = filtered.length;
    const showingAll = pageSize === 0;
    const effectivePerPage = showingAll ? Math.max(total, 1) : pageSize;
    const pageCount = showingAll ? 1 : Math.max(1, Math.ceil(total / effectivePerPage));
    const safePage = Math.min(Math.max(1, currentPage), pageCount);
    const visible = useMemo(() => {
        if (showingAll) return filtered;
        const start = (safePage - 1) * effectivePerPage;
        return filtered.slice(start, start + effectivePerPage);
    }, [filtered, showingAll, safePage, effectivePerPage]);

    // Reset to page 1 when the filter input or page-size changes — otherwise
    // a user on page 3 who types a search returning 5 items would land on a
    // visually empty page.
    useEffect(() => {
        setCurrentPage(1);
    }, [search, pageSize]);

    // Normalise the picker source — responsibilities may come back as either
    // {value, label} or {id, label}, depending on which resource shape backend
    // serialised. The shared ProcessPicker expects {id, label}.
    const pickerOptions: ProcessPickerOption[] = useMemo(() => {
        return (props.responsibilities || [])
            .map(r => ({
                id: Number(r.id ?? r.value ?? 0),
                label: r.label ?? '',
            }))
            .filter(o => o.id > 0);
    }, [props.responsibilities]);

    // Build the ordered selection that drives ProcessOrderStrip. We preserve
    // the order of modalResponsibilityIds (that's the production order the
    // user has chosen) and drop any IDs that don't correspond to a known
    // responsibility option — defensive against stale IDs that might have
    // come back from a previous edit if a responsibility was deleted on the
    // server in the meantime.
    const selectedStripItems: ProcessOrderStripItem[] = useMemo(() => {
        const optionsById = new Map<number, ProcessPickerOption>();
        pickerOptions.forEach(o => optionsById.set(o.id, o));
        return modalResponsibilityIds
            .map(id => optionsById.get(id))
            .filter((o): o is ProcessPickerOption => Boolean(o))
            .map(o => ({ id: o.id, label: o.label }));
    }, [modalResponsibilityIds, pickerOptions]);

    // Strip reorder / remove → recompute the ID array from the new chip order.
    const handleStripChange = (next: ProcessOrderStripItem[]) => {
        setModalResponsibilityIds(next.map(item => item.id));
    };

    // When the user clicks "Edit" we dispatch getMaterial(id). This fills
    // selectedMaterial in redux with {value, label, responsibilities: [...]}.
    // Once it arrives for the material we're editing, populate the modal.
    useEffect(() => {
        if (modalMode !== 'edit' || !editingMaterial) return;
        const incoming = props.selectedMaterial;
        if (!incoming) return;
        const incomingId = Number(incoming.value);
        if (incomingId !== editingMaterial.id) return;
        if (populatedFromId === incomingId) return;

        setModalName(incoming.label || editingMaterial.material || '');
        const ids = (incoming.responsibilities || [])
            .map(r => Number(r.id ?? r.value ?? 0))
            .filter(id => id > 0);
        setModalResponsibilityIds(ids);
        setModalLoading(false);
        setPopulatedFromId(incomingId);
    }, [props.selectedMaterial, modalMode, editingMaterial, populatedFromId]);

    const handleStartCreate = () => {
        setModalMode('create');
        setEditingMaterial(null);
        setModalName('');
        setModalResponsibilityIds([]);
        setModalLoading(false);
        setPopulatedFromId(null);
    };

    const handleStartEdit = (material: MaterialDetailed) => {
        setModalMode('edit');
        setEditingMaterial(material);
        // Pre-fill the name immediately so the modal isn't blank while the
        // responsibilities fetch is in flight.
        setModalName(material.material || '');
        setModalResponsibilityIds([]);
        setModalLoading(true);
        setPopulatedFromId(null);
        props.adminActions.getMaterial(material.id);
    };

    const handleCloseModal = () => {
        setModalMode('closed');
        setEditingMaterial(null);
        setModalName('');
        setModalResponsibilityIds([]);
        setModalLoading(false);
        setPopulatedFromId(null);
    };

    const handleSave = () => {
        const name = modalName.trim();
        if (!name) {
            toast.error(t('admin.materials.toastFillName'));
            return;
        }
        const payload = {
            material: name,
            responsibilities: modalResponsibilityIds,
        };

        if (modalMode === 'create') {
            props.adminActions.createMaterial(payload).then(() => {
                handleCloseModal();
                refresh();
            });
        } else if (modalMode === 'edit' && editingMaterial) {
            props.adminActions.editMaterial(editingMaterial.id, payload).then(() => {
                handleCloseModal();
                refresh();
            });
        }
    };

    const handleDelete = (material: MaterialDetailed) => {
        if (!material.can_delete) {
            toast.error(t('admin.materials.toast.hasArticles'));
            return;
        }
        setDeleteConfirm(material);
    };

    const confirmDelete = () => {
        if (!deleteConfirm) return;
        const id = deleteConfirm.id;
        props.adminActions.deleteMaterial(id).then(() => {
            setDeleteConfirm(null);
            refresh();
        });
    };

    const formatDate = (iso: string | null) => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString(intl.locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const modalTitle = modalMode === 'edit'
        ? fm('admin.materials.editTitle', 'Ndrysho materialin')
        : fm('admin.materials.createTitle', 'Krijo material të ri');

    return (
        <div className="mat-page">
            <MikaPageHeader
                title={<FormattedMessage id="admin.materials.pageTitle" defaultMessage="Materialet" />}
                subtitle={fm('admin.materials.pageSubtitle', 'Menaxho materialet dhe proceset që zbatohen mbi to')}
                actions={
                    <button
                        type="button"
                        className="mat-btn mat-btn--primary"
                        onClick={handleStartCreate}
                        disabled={modalMode !== 'closed' || props.buttonLoader}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5v14" />
                        </svg>
                        <FormattedMessage id="admin.materials.createBtn" defaultMessage="Krijo material" />
                    </button>
                }
            />

            <div className="mat-toolbar">
                <div className="mat-search">
                    <svg className="mat-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        value={search}
                        placeholder={fm('admin.materials.searchPlaceholder', 'Kërko material...')}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <div className="mat-toolbar__count">
                    <FormattedMessage
                        id="admin.materials.countTotal"
                        defaultMessage="{count, plural, one {# material} other {# materiale}}"
                        values={{ count: filtered.length }}
                    />
                </div>
            </div>

            <div className={`mat-list ${props.kubitEnabled ? 'mat-list--kubit' : ''}`}>
                <div className="mat-list__header">
                    <div className="mat-list__col mat-list__col--name">
                        <FormattedMessage id="admin.materials.col.name" defaultMessage="Materiali" />
                    </div>
                    <div className="mat-list__col mat-list__col--num">
                        <FormattedMessage id="admin.materials.col.procese" defaultMessage="Procese" />
                    </div>
                    <div className="mat-list__col mat-list__col--num">
                        <FormattedMessage id="admin.materials.col.articles" defaultMessage="Artikuj" />
                    </div>
                    <div className="mat-list__col mat-list__col--date">
                        <FormattedMessage id="admin.materials.col.created" defaultMessage="Krijuar" />
                    </div>
                    {props.kubitEnabled && (
                        <div className="mat-list__col mat-list__col--kubit">
                            <FormattedMessage id="admin.materials.col.kubit" defaultMessage="Materiali KuBIT" />
                        </div>
                    )}
                    <div className="mat-list__col mat-list__col--actions" />
                </div>

                {isInitialLoad ? (
                    [0, 1, 2, 3, 4].map(i => (
                        <div key={`sk-${i}`} className="mat-row mat-row--skeleton">
                            <div className="mat-row__cell mat-row__cell--name">
                                <div className="mat-skeleton mat-skeleton--icon" />
                                <div className="mat-skeleton mat-skeleton--name" />
                            </div>
                            <div className="mat-row__cell mat-row__cell--num"><div className="mat-skeleton mat-skeleton--num" /></div>
                            <div className="mat-row__cell mat-row__cell--num"><div className="mat-skeleton mat-skeleton--num" /></div>
                            <div className="mat-row__cell mat-row__cell--date"><div className="mat-skeleton mat-skeleton--date" /></div>
                            {props.kubitEnabled && <div className="mat-row__cell mat-row__cell--kubit"><div className="mat-skeleton mat-skeleton--date" /></div>}
                            <div className="mat-row__cell mat-row__cell--actions" />
                        </div>
                    ))
                ) : (
                    <>
                        {filtered.length === 0 && (
                            <div className="mat-empty">
                                {search.trim() ? (
                                    <FormattedMessage id="admin.materials.empty.search" defaultMessage="Asnjë material nuk përputhet me kërkimin." />
                                ) : (
                                    <FormattedMessage id="admin.materials.empty.list" defaultMessage="Nuk ka materiale. Krijoni një të ri për të filluar." />
                                )}
                            </div>
                        )}

                        {visible.map(material => {
                            const initial = ((material.material || '?').charAt(0) || '?').toUpperCase();
                            return (
                                <div key={material.id} className="mat-row">
                                    <div className="mat-row__cell mat-row__cell--name">
                                        <div className="mat-row__icon">{initial}</div>
                                        <div className="mat-row__name">{material.material || '—'}</div>
                                    </div>
                                    <div className="mat-row__cell mat-row__cell--num">{material.responsibilities_count}</div>
                                    <div className="mat-row__cell mat-row__cell--num">{material.articles_count}</div>
                                    <div className="mat-row__cell mat-row__cell--date">{formatDate(material.created_at)}</div>
                                    {props.kubitEnabled && (
                                        <div className="mat-row__cell mat-row__cell--kubit">
                                            <select
                                                className={`mat-kubit-select ${kubitLinks[material.id] ? 'is-linked' : ''}`}
                                                value={kubitLinks[material.id] ?? ''}
                                                onChange={(e) => handleKubitLink(material.id, e.target.value === '' ? null : Number(e.target.value))}
                                            >
                                                <option value="">{fm('admin.materials.kubit.unlinked', '— Pa lidhje —')}</option>
                                                {kubitItems.map((it) => (
                                                    <option key={it.value} value={it.value}>
                                                        {it.label}{it.code ? ` (${it.code})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div className="mat-row__cell mat-row__cell--actions">
                                        <button
                                            type="button"
                                            className="mat-icon-btn"
                                            onClick={() => handleStartEdit(material)}
                                            title={fm('common.action.edit', 'Ndrysho')}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            className="mat-icon-btn mat-icon-btn--danger"
                                            onClick={() => handleDelete(material)}
                                            title={material.can_delete ? fm('common.action.delete', 'Fshi') : fm('admin.materials.toast.hasArticles', 'Nuk mund ta fshish — material në prodhim')}
                                            disabled={!material.can_delete}
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="3 6 5 6 21 6" />
                                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="mat-add-row" onClick={handleStartCreate}>
                            <div className="mat-row__icon mat-row__icon--add">+</div>
                            <div className="mat-add-row__label">
                                <FormattedMessage id="admin.materials.addNew" defaultMessage="Krijo material të ri" />
                            </div>
                        </div>

                        <PaginationComponent
                            currentPage={safePage}
                            perPage={pageSize}
                            pagination={{
                                currentPage: safePage,
                                lastPage: pageCount,
                                perPage: effectivePerPage,
                                total,
                            }}
                            onPageChange={setCurrentPage}
                            onPerPageChange={setPageSize}
                            label={fm('admin.materials.pagination.label', 'materiale')}
                        />
                    </>
                )}
            </div>

            {modalMode !== 'closed' && (
                <div className="mat-modal-overlay" onClick={handleCloseModal}>
                    <div
                        className="mat-modal"
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="mat-modal__head">
                            <h3 className="mat-modal__title">{modalTitle}</h3>
                            <button
                                type="button"
                                className="mat-modal__close"
                                onClick={handleCloseModal}
                                aria-label={fm('common.action.cancel', 'Anulo')}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <div className="mat-modal__body">
                            <div className="mat-modal__field">
                                <label className="mat-modal__label" htmlFor="mat-name">
                                    <FormattedMessage id="admin.materials.modal.nameLabel" defaultMessage="Emri i materialit" />
                                </label>
                                <input
                                    id="mat-name"
                                    type="text"
                                    className="mat-modal__input"
                                    value={modalName}
                                    placeholder={fm('admin.materials.placeholderName', 'Emri i materialit...')}
                                    onChange={e => setModalName(e.target.value)}
                                    autoFocus={modalMode === 'create'}
                                />
                            </div>

                            <div className="mat-modal__field">
                                <label className="mat-modal__label">
                                    <FormattedMessage id="admin.materials.modal.processesLabel" defaultMessage="Proceset që zbatohen" />
                                </label>
                                {modalLoading ? (
                                    <div className="mat-modal__loader">
                                        <FormattedMessage id="admin.materials.modal.loading" defaultMessage="Duke ngarkuar..." />
                                    </div>
                                ) : (
                                    <div className="mat-modal__processes">
                                        <ProcessOrderStrip
                                            selected={selectedStripItems}
                                            onChange={handleStripChange}
                                        />
                                        <div className="mat-modal__picker">
                                            <ProcessPicker
                                                options={pickerOptions}
                                                selectedIds={modalResponsibilityIds}
                                                onChange={setModalResponsibilityIds}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mat-modal__foot">
                            <button
                                type="button"
                                className="mat-modal-btn mat-modal-btn--cancel"
                                onClick={handleCloseModal}
                            >
                                <FormattedMessage id="common.action.cancel" defaultMessage="Anulo" />
                            </button>
                            <button
                                type="button"
                                className="mat-modal-btn mat-modal-btn--save"
                                onClick={handleSave}
                                disabled={props.buttonLoader || modalLoading}
                            >
                                {modalMode === 'edit' ? (
                                    <FormattedMessage id="common.action.save" defaultMessage="Ruaj" />
                                ) : (
                                    <FormattedMessage id="common.action.create" defaultMessage="Krijo" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MikaConfirmModal
                isOpen={deleteConfirm !== null}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={confirmDelete}
                title={fm('admin.materials.deleteTitle', 'Fshi materialin')}
                message={intl.formatMessage(
                    { id: 'admin.materials.deleteConfirm', defaultMessage: 'A jeni të sigurt që doni të fshini {name}?' },
                    { name: '"' + (deleteConfirm?.material || '') + '"' }
                )}
                confirmLabel={fm('common.action.delete', 'Fshi')}
                confirmColor="danger"
            />
        </div>
    );
};

function mapGlobalStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        loading: state.app.loading,
        buttonLoader: state.app.buttonLoader,
        materialsDetailed: (state.admin as any).materialsDetailed || [],
        responsibilities: (state as any).production?.responsibilities || [],
        selectedMaterial: (state.admin as any).selectedMaterial || null,
        kubitEnabled: state.app.companyProfile?.kubitEnabled,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        productionActions: bindActionCreators(productionActions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch),
    };
}

export default withRouter(connect(mapGlobalStateToProps, mapDispatchToProps)(Materials as any));
